import {
    useCallback,
    useEffect,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
    matchPath,
    useLocation,
} from 'react-router';

import useProductName from '../../common/hooks/useProductName';
import { isHostModeSelector } from '../../store/selectors';
import buildTitleMap from './Constant/buildTitleMap';

const findMatchingRouteKey = (pathname: string, routesMap: Map<string, string>): string | null => {
    for (const key of routesMap.keys()) {
        const match = matchPath({ path: key }, pathname);

        if (match) {
            return key;
        }
    }

    return null;
};

export const useTabName = () => {
    const { formatMessage: translate } = useIntl();
    const location = useLocation();
    const hostMode = useSelector(isHostModeSelector);
    // PLT-79155 — Test Cloud orgs need their own product name in the page title
    // ("Home - Test Cloud") instead of the deployment-level "Automation Cloud"
    // from window.env. useProductName already encapsulates that detection
    // (entitlements + feature flag + admin role) and is the source of truth
    // PrivateShell uses for the shell header product label, so we reuse it
    // here for the browser tab title to keep both in sync.
    const { productName } = useProductName();

    const getTitle = useCallback(
        (pathname: string) => {
            const includesPathTitle = buildTitleMap(hostMode);
            const baseRoute = window.env.tabNameRoute + '/';
            const routeString = pathname.substr(pathname.indexOf(baseRoute) + baseRoute.length).toLowerCase();
            const matchingKey: string | null = findMatchingRouteKey(`/${routeString}`, includesPathTitle);
            if (!window.env.useOldRoutes) {
                if (matchingKey) {
                    return translate({ id: includesPathTitle.get(matchingKey) }, { productName });
                }
                // fallback case, return product name when no match found
                return productName;
            }

            const path = routeString.substring(0, routeString.indexOf('/')) || routeString.substring(0);
            return includesPathTitle.get(path)
                ? translate({ id: includesPathTitle.get(path) }, { productName })
                : productName;
        },
        [ hostMode, translate, productName ],
    );

    useEffect(() => {
        document.title = getTitle(location.pathname);
    }, [ getTitle, location.pathname ]);

    return { getTitle };
};
